import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { getTopic, TOPICS } from '../data/topics';

export default function TopicPage() {
  const { slug } = useParams();
  const topic = getTopic(slug);

  if (!topic) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6 px-6" data-testid="topic-not-found">
        <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>Page Not Found</h1>
        <p className="text-gray-600">We couldn't find that topic.</p>
        <Link to="/">
          <Button className="bg-[#FF8C00] hover:bg-[#e07d00] text-white" data-testid="topic-back-home-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back Home
          </Button>
        </Link>
      </div>
    );
  }

  const related = (topic.related || []).map((s) => ({ slug: s, t: TOPICS[s] })).filter((r) => r.t);

  return (
    <div className="min-h-screen" data-testid="topic-page">
      {/* Hero */}
      <section className="relative h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${topic.heroImage}')` }}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30"></div>
          <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 80% 20%, ${topic.accent}30 0%, transparent 55%)` }}></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 pb-12 w-full text-white">
          <Badge className="mb-4 text-white border-0" style={{ backgroundColor: topic.accent }} data-testid="topic-category-badge">
            {topic.category}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }} data-testid="topic-title">
            {topic.title}
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl">{topic.tagline}</p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto space-y-6">
          {topic.overview.map((p, i) => (
            <p key={i} className="text-lg md:text-xl text-gray-700 leading-relaxed">{p}</p>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-6 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8">
          {topic.stats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2" style={{ color: i % 2 === 0 ? '#00CED1' : '#FF8C00', fontFamily: 'Montserrat, sans-serif' }}>
                {s.value}
              </div>
              <p className="text-gray-300 text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            What This Means in Practice
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {topic.highlights.map((h, i) => (
              <Card key={i} className="hover:shadow-xl transition-all duration-300 border-l-4" style={{ borderLeftColor: topic.accent }}>
                <CardContent className="p-6 flex gap-4">
                  <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: topic.accent }} />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{h.t}</h3>
                    <p className="text-gray-600">{h.d}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Initiative */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden border-2" style={{ borderColor: topic.accent }}>
            <div className="h-2" style={{ backgroundColor: topic.accent }}></div>
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-7 h-7" style={{ color: topic.accent }} />
                <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {topic.initiative.title}
                </h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">{topic.initiative.text}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Related + CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-6xl mx-auto">
          {related.length > 0 && (
            <>
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Explore Further
              </h2>
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {related.map((r) => (
                  <Link key={r.slug} to={`/explore/${r.slug}`} data-testid={`related-link-${r.slug}`}>
                    <Card className="bg-white/10 border-gray-700 hover:border-[#00CED1] hover:bg-white/15 transition-all duration-300 h-full overflow-hidden group">
                      <div className="relative h-32 overflow-hidden">
                        <img src={r.t.heroImage} alt={r.t.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      </div>
                      <CardContent className="p-5">
                        <h3 className="text-lg font-bold mb-1" style={{ color: r.t.accent }}>{r.t.title}</h3>
                        <p className="text-gray-300 text-sm flex items-center gap-2">
                          {r.t.category} <ArrowRight className="w-4 h-4" />
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          )}
          <div className="text-center">
            <p className="text-xl text-gray-300 mb-6">Ready to engage on {topic.title}?</p>
            <Link to="/engage">
              <Button size="lg" className="bg-[#FF8C00] hover:bg-[#e07d00] text-white font-bold px-10 transition-all duration-300 transform hover:scale-105" data-testid="topic-engage-cta">
                Engage With Us <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
